import React from 'react';
import dynamic from 'next/dynamic';
import { AgencyInfoSection } from '../../AgencyInfoSection';
import { OwnerInfoSection } from '../../OwnerInfoSection';
import { ResidenceAdditionalInfo } from '../../ResidenceAdditionalInfo';
import { SpaceSection } from '../../../SpaceSection';
import useScreenSize from '../../../../../hooks/useScreenSize';
import { PhoneButtonClickHandler } from '../../../BottomPhoneButton/types';
import { AlertMessage } from '../../AlertMessage';
import { AgencyAdditionalInfo } from '../../AgencyAdditionalInfo';
import { AgencyInfoWrapper } from './styled';
import { useClient } from '../../../../../hooks/useClient';
import { DeskBlock } from '../../CurrentItemSections/styled';
import { PlaceholderBlock } from '../../../../../styled/placeholders.styled';
import { FullItemData } from '../../CurrentItemSections/types';

interface OwnerContactsBlockProps {
  isAgency: boolean;
  item: FullItemData;
  isPublished: boolean;
  isAuthor: boolean;
  isResidence: boolean;
  maskedPhones?: string[];
  onClickHandler: PhoneButtonClickHandler;
  phonesShown: string[] | null;
  isVisible?: boolean;
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const PhonesSection = dynamic(
  () => import('../../PhonesSection').then(mod => mod.PhonesSection),
  {
    ssr: false
  }
);

const BottomPhoneButton = dynamic(
  () => import('../../../BottomPhoneButton').then(mod => mod.BottomPhoneButton),
  {
    ssr: false,
    loading: () => <PlaceholderBlock height="60px" margin="0" />
  }
);

export const OwnerContactsBlock: React.FC<OwnerContactsBlockProps> = ({
  isAgency,
  item,
  isPublished,
  isAuthor,
  isResidence,
  maskedPhones,
  onClickHandler,
  phonesShown,
  isVisible,
  setIsVisible
}) => {
  const { isSmallOrLargeDesktop } = useScreenSize();
  const isClient = useClient();

  const business = item?.business;
  const businessAgency = business?.__typename === 'Agency' ? business : null;
  const businessResidence =
    business?.__typename === 'Residence' ? business : null;

  const ownerName = isResidence
    ? businessResidence?.companyName
    : item?.contactName;
  const ownership = isResidence ? null : item?.contactTypeName;
  const logoUrl = isResidence ? businessResidence?.logoUrl : null;
  const showOnDesk = !isClient || isSmallOrLargeDesktop;

  const shouldRenderPhoneButtonOnDesktop =
    showOnDesk && (isPublished || isAuthor);

  const agencyInfo = (
    <AgencyInfoSection
      path={item?.business?.path || ''}
      name={businessAgency?.name}
      imageUrl={item?.business?.logoUrl}
    />
  );

  const filteredApartments = businessResidence?.apartments?.nodes?.filter(
    node => !!node
  );

  return (
    <SpaceSection>
      {isAgency && !showOnDesk && (
        <AgencyInfoWrapper>{agencyInfo}</AgencyInfoWrapper>
      )}

      <OwnerInfoSection
        ownerName={ownerName}
        ownership={ownership}
        logoUrl={logoUrl}
        isAgency={isAgency}
        isResidence={isResidence}
      />

      {shouldRenderPhoneButtonOnDesktop ? (
        <DeskBlock>
          <BottomPhoneButton
            stat="product-call-btn"
            phones={isAuthor ? maskedPhones : phonesShown}
            clickHandler={onClickHandler}
            {...{ setIsVisible, isVisible, isAuthor }}
          />
        </DeskBlock>
      ) : (
        <PhonesSection
          phonesMasked={maskedPhones}
          onClickHandler={onClickHandler}
          phonesShown={isAuthor ? maskedPhones : phonesShown}
          isAuthor={isAuthor}
        />
      )}

      {!isAgency && !isResidence && !isAuthor && !!phonesShown?.length && (
        <AlertMessage />
      )}

      {isPublished && isAgency && (
        <AgencyAdditionalInfo
          linkPath={item?.business?.path}
          agencyInfo={agencyInfo}
          offersCount={businessAgency?.itemsCount}
          schedule={item?.business?.schedule}
          address={businessAgency?.contactAddress}
          latitude={businessAgency?.latitude}
          longitude={businessAgency?.longitude}
          city={item?.business?.city?.name}
        />
      )}

      {isPublished && isResidence && (
        <ResidenceAdditionalInfo
          linkPath={item?.business?.path}
          website={businessResidence?.website}
          contactEmail={businessResidence?.contactEmail}
          schedule={item?.business?.schedule}
          address={businessResidence?.address}
          latitude={businessResidence?.lat}
          longitude={businessResidence?.lng}
          apartments={filteredApartments}
          priceMin={businessResidence?.price?.total?.minimum}
          currency={businessResidence?.price?.currency}
          deadline={businessResidence?.deadline}
          hasBillOfSale={businessResidence?.hasBillOfSale}
          hasInternalLoan={item?.hasInternalLoan}
          city={item?.business?.city?.name}
        />
      )}
    </SpaceSection>
  );
};
